var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Trans, t } from '@lingui/macro';
import { AvatarLetter, AvatarWithLabel, Button, IconButton, Loader, Pagination, PaginationPage, TextArea, Tooltip, Typography, themeGet, } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import gql from 'graphql-tag';
import styled, { css } from 'styled-components';
import { z } from 'zod';
import { getReviewPluginMarkerRep, highlightInDoc, withReviewPlugin, } from 'shared/domains/documents/components/Preview/Pleditor/plugins/Review';
import { elementIndex } from 'shared/domains/documents/components/Preview/Pleditor/plugins/Review/doc';
import { Actions } from './Actions';
import { useGetHighlightInDocument } from './highlightInDocument';
import { useUserName } from './useUserName';
import { Markdown } from '../Markdown';
// ---- GraphQL
const CHAT_WITH_DOCUMENT_REVIEW_MUTATION = gql `
  mutation ChatWithDocumentReview($messages: [ReviewChatMessageInput!]!, $feedbackId: ID!) {
    chatWithDocumentReview(messages: $messages, feedbackId: $feedbackId) {
      token
    }
  }
`;
const zServerSentMessage = z.tuple([
    z.string(),
    z.tuple([z.string(), z.string()]),
]);
// ---- Hooks
// This implements a temporary chat system for the review chat.
// It maintains ephemeral client side chat message state which is sent to the server.
// The server is currently stateless in regards to review chat conversations.
const useChat = (chatRequest) => {
    const [messages, setMessages] = useState([]);
    const [actions] = useState([]);
    // Functions derived from setMessages
    const appendToLastMessage = useCallback((text) => {
        setMessages(msgs => {
            const lastMessage = msgs[msgs.length - 1];
            if (lastMessage) {
                return [
                    ...msgs.slice(0, -1),
                    Object.assign(Object.assign({}, lastMessage), { message: lastMessage.message + text }),
                ];
            }
            return msgs;
        });
    }, [setMessages]);
    const setLastMessageDone = useCallback(() => {
        setMessages(msgs => {
            const lastMessage = msgs[msgs.length - 1];
            if (lastMessage) {
                return [
                    ...msgs.slice(0, -1),
                    Object.assign(Object.assign({}, lastMessage), { writing: false }),
                ];
            }
            return msgs;
        });
    }, [setMessages]);
    const appendMessages = useCallback((message) => setMessages(prev => [...prev, ...message]), [setMessages]);
    return {
        messages,
        actions,
        chat: useCallback((userPrompt) => __awaiter(void 0, void 0, void 0, function* () {
            const REVIEW_CHAT_URL = process.env.ASSISTANT_CHAT_STREAM_URL;
            // Append user message and system message target for chunk appending
            // When sending to API we only include the user message
            const newMessages = [
                {
                    role: 'user',
                    message: userPrompt,
                    writing: false,
                },
                {
                    role: 'assistant',
                    message: '',
                    writing: true,
                },
            ];
            appendMessages(newMessages);
            // Request chat message and retrieve token for chat stream
            // ! We include the user chat message, but not the assistant pending message
            const token = yield chatRequest([...messages, newMessages[0]]);
            if (token) {
                // Open SSE stream for chat messages
                const evtSource = new EventSource(`${REVIEW_CHAT_URL}/review-chat/${token}`);
                evtSource.onmessage = event => {
                    // TODO - ACTUALLY PARSE - ASK FOR ZOD
                    const [, [kind, content]] = zServerSentMessage.parse(JSON.parse(event.data)); // ! TODO: Safe parse
                    switch (kind) {
                        case 'MSG':
                            appendToLastMessage(content);
                            break;
                        case 'ACTION': {
                            // HANDLE ACTIONS HERE
                            break;
                        }
                        default:
                            break;
                    }
                };
                // ! The server will close the connection after sending the end token,
                // ! this is treated as an error state by the EventSource, and we thus
                // ! close the connection.
                evtSource.onerror = () => {
                    evtSource.close();
                    setLastMessageDone();
                };
            }
        }), [appendMessages, chatRequest, messages, appendToLastMessage, setLastMessageDone]),
    };
};
// Specialized chat hooks for review chat
const useChatWithDocumentReview = (feedbackId) => {
    const [chatWithDocumentReview] = useMutation(CHAT_WITH_DOCUMENT_REVIEW_MUTATION);
    return useChat((messages) => chatWithDocumentReview({
        variables: {
            feedbackId,
            messages: messages.map(({ role, message }) => ({ role, message })),
        },
    }).then(res => { var _a, _b, _c; return (_c = (_b = (_a = res.data) === null || _a === void 0 ? void 0 : _a.chatWithDocumentReview) === null || _b === void 0 ? void 0 : _b.token) !== null && _c !== void 0 ? _c : ''; }));
};
// ---- StyledComponents
const Container = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${themeGet('spacing.lg')};
  padding: ${themeGet('spacing.xl')} ${themeGet('spacing.xl')} ${themeGet('spacing.2xl')};
  height: 100%;
`;
const Thread = styled.div `
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${themeGet('spacing.3xl')};
  justify-content: flex-end;
  min-height: 100%;
`;
const Scroller = styled.div `
  overflow: hidden scroll;
  height: 100%;

  & * {
    overflow-anchor: none;
  }
`;
const StyledMarkdown = styled(Markdown) `
  display: inline;
  code {
    background-color: ${themeGet('colors.gray.50')};
    color: ${themeGet('colors.assistant.700')};
    font-family: ${themeGet('fontFamily.default')};
    display: block;
    margin: ${themeGet('spacing.lg')};
    box-shadow: ${themeGet('dropShadow.100')};
    border-radius: ${themeGet('borderRadius.md')};
    padding: ${themeGet('spacing.sm')};
    white-space: break-spaces;
  }
  ${props => props.$writing &&
    css `
      & > *:last-child::after {
        content: '|';
        font-weight: ${themeGet('fontWeight.extrabold')};
        color: ${themeGet('colors.assistant.500')};
      }
    `}
`;
const Message = styled.div `
  display: flex;
  flex-direction: column;

  ${({ $direction }) => $direction === 'right' &&
    css `
      align-items: flex-end;
      text-align: right;
    `};
`;
const SuggestionsSpacer = styled.div `
  display: flex;
  gap: ${themeGet('spacing.md')};
  flex-flow: row wrap;
`;
const TextAreaSpacer = styled.div `
  display: flex;
  gap: ${themeGet('spacing.md')};
  flex-direction: column;
  align-items: end;
`;
const StyledAvatarWithLabel = styled(AvatarWithLabel).attrs({ size: 'md' }) `
  margin-bottom: ${themeGet('spacing.lg')};
`;
// https://css-tricks.com/books/greatest-css-tricks/pin-scrolling-to-bottom/
// eslint-disable-next-line no-restricted-syntax
const ScrollAnchor = styled.div `
  overflow-anchor: auto;
  height: 1px;
`;
const StyledHorizontalLine = styled.span `
  height: 0.1rem;
  width: 100%;
  background-color: ${themeGet('colors.gray.100')};
`;
const StyledHighlighterContainer = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${themeGet('spacing.md')};
  min-width: 100%;
  place-items: center;
`;
const StyledHighlightLabel = styled(Typography) `
  display: flex;
  align-items: center;
  gap: ${themeGet('spacing.sm')};
`;
// ---- Components
function Highlighter({ highlights }) {
    const [index, setIndex] = useState(0);
    const teardown = useRef();
    useEffect(() => () => { var _a; return (_a = teardown.current) === null || _a === void 0 ? void 0 : _a.call(teardown); }, [teardown]);
    return (<StyledHighlighterContainer>
      <StyledHorizontalLine />
      <StyledHighlightLabel>
        <Trans comment="Review chat - highlighter - show in document">Show in document</Trans>
      </StyledHighlightLabel>
      <Pagination prev={() => {
            var _a;
            if (index > 0) {
                (_a = teardown.current) === null || _a === void 0 ? void 0 : _a.call(teardown);
                teardown.current = highlights[index - 1].highlightOn();
                setIndex(index - 1);
            }
        }} next={() => {
            var _a;
            if (index < highlights.length - 1) {
                (_a = teardown.current) === null || _a === void 0 ? void 0 : _a.call(teardown);
                teardown.current = highlights[index + 1].highlightOn();
                setIndex(index + 1);
            }
        }}>
        {highlights.map(({ highlightOn: highlight, text }, i) => (<PaginationPage key={text} $selected={index === i} onClick={() => {
                var _a;
                (_a = teardown.current) === null || _a === void 0 ? void 0 : _a.call(teardown);
                teardown.current = highlight();
                setIndex(i);
            }}>
            {i + 1}
          </PaginationPage>))}
      </Pagination>
    </StyledHighlighterContainer>);
}
export const useDocumentHighlights = (feedback) => {
    const [docHighlights, setDocHighlights] = useState({ type: 'NotAsked' });
    const { getHighlightInDocument } = useGetHighlightInDocument();
    const getHighlights = useCallback((message) => __awaiter(void 0, void 0, void 0, function* () {
        withReviewPlugin(plugin => {
            const root = plugin.editor.model.document.getRoot();
            if (!root)
                return;
            const ctx = elementIndex(root);
            const query = [
                feedback.feedback.name,
                feedback.feedback.notes,
                feedback.feedback.content,
                message,
            ].join('\n');
            const texts = ctx.entries.map(entry => entry.text);
            setDocHighlights({ type: 'Loading' });
            getHighlightInDocument(query, texts).then(highlights => {
                setDocHighlights({
                    type: 'Loaded',
                    data: (highlights !== null && highlights !== void 0 ? highlights : [])
                        .filter(x => x.score > 0.5) // Highly arbitrary for now
                        .slice(0, 5)
                        .flatMap(match => {
                        const entry = ctx.entries[match.index];
                        if (!entry)
                            return [];
                        const node = entry.path[entry.path.length - 1];
                        if (!node)
                            return [];
                        const markerRep = getReviewPluginMarkerRep('highlight');
                        const isActive = () => plugin.editor.model.markers.get(markerRep.name) !== null;
                        const removeMarker = () => plugin.editor.model.change(writer => writer.removeMarker(markerRep.name));
                        return [
                            {
                                text: entry.text,
                                isActive,
                                highlightOff: () => {
                                    if (isActive()) {
                                        removeMarker();
                                    }
                                },
                                highlightOn: (scroll = true) => {
                                    if (isActive())
                                        return () => { };
                                    highlightInDoc(node, markerRep);
                                    if (scroll) {
                                        if (node.is('element')) {
                                            const viewNode = plugin.editor.editing.mapper.toViewElement(node);
                                            const dom = plugin.editor.editing.view.domConverter.viewToDom(viewNode);
                                            dom.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'center',
                                            });
                                        }
                                    }
                                    return removeMarker;
                                },
                            },
                        ];
                    }),
                });
            });
        });
    }), [getHighlightInDocument, feedback]);
    return { docHighlights, getHighlights };
};
// Internal common component
function Chat({ feedback, send, messages, actions, userName, }) {
    const [userMessage, setUserMessage] = useState('');
    const isStreaming = Boolean(messages.find(({ writing }) => writing));
    const { getHighlights, docHighlights } = useDocumentHighlights(feedback);
    const resetMessage = () => setUserMessage('');
    const sendMessage = (controlledMessage) => {
        const message = controlledMessage || userMessage;
        send(message).then(resetMessage);
        getHighlights(message);
    };
    const sendSuggestion = (suggestion) => sendMessage(suggestion);
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !isStreaming) {
            event.preventDefault();
            sendMessage();
        }
    };
    const actionNames = {
        highlight: t({ comment: 'Review chat - action name - highlight', message: 'Highlight' }),
    };
    const SUGGESTIONS = [
        {
            name: t({
                comment: 'Review chat - suggestions - explain further',
                message: 'Explain further',
            }),
            message: 'Please explain this issue in more detail.',
        },
        {
            name: t({ comment: 'Review chat - suggestions - rewrite clause', message: 'Rewrite clause' }),
            message: 'Please write me a suggestion for how to rewrite the most relevant clause for this issue.',
        },
        {
            name: t({
                comment: 'Review chat - suggestions - show playbook reference',
                message: 'Show playbook reference',
            }),
            message: 'Please cite the relevant playbook rule related to this issue.',
        },
    ];
    const anchorRef = useRef(null);
    useEffect(() => {
        if (anchorRef.current)
            anchorRef.current.scrollIntoView();
    }, [anchorRef]);
    return (<Container>
      <Scroller>
        <Thread>
          {messages.map(({ role, message, writing }, i) => (<Message key={
            // eslint-disable-next-line react/no-array-index-key
            i} $direction={role === 'assistant' ? 'left' : 'right'}>
              {role === 'assistant' ? (<StyledAvatarWithLabel solid appearance="ai" title={t({
                    message: 'plai',
                    comment: 'Review chat - message - assistant name',
                })}>
                  <Falcon icon="sparkles"/>
                </StyledAvatarWithLabel>) : (<StyledAvatarWithLabel solid appearance="orange" title={userName}>
                  <AvatarLetter>{userName.slice(0, 1)}</AvatarLetter>
                </StyledAvatarWithLabel>)}
              {message === '' ? (<Loader />) : (<Typography>
                  <StyledMarkdown contents={message} $writing={writing ? true : undefined}/>
                </Typography>)}
              {!writing && message !== '' && role === 'assistant' && (<Actions actions={['COPY']} message={message}/>)}
            </Message>))}
        </Thread>
        <ScrollAnchor key="scroll-anchor" ref={anchorRef}/>
      </Scroller>
      <SuggestionsSpacer>
        {actions.map(action => (<Button onClick={() => { }} key={action.type} size="small" appearance="primary" disabled={isStreaming}>
            {actionNames[action.type]}
          </Button>))}
        {SUGGESTIONS.map(suggestion => (<Button key={suggestion.name} size="small" appearance="secondary" onClick={() => sendSuggestion(suggestion.message)} disabled={isStreaming}>
            {suggestion.name}
          </Button>))}
        {docHighlights.type === 'Loaded' && docHighlights.data.length > 0 && (<Highlighter highlights={docHighlights.data}/>)}
      </SuggestionsSpacer>
      <TextAreaSpacer>
        <TextArea onKeyDown={handleKeyDown} value={userMessage} onChange={ev => setUserMessage(ev.target.value)} label={t({ message: 'Ask a question', comment: 'Review chat - textarea - label' })} variant="line" stretch adornmentRight={<IconButton appearance="ai" size="small" onClick={() => sendMessage()} disabled={isStreaming} aria-label={t({
                message: 'Send',
                comment: 'Review chat - textarea - send button - aria label',
            })}>
              <Falcon icon="arrow-up"/>
            </IconButton>}/>
        <Tooltip position="left" title={t({
            comment: 'Review chat - prompt library - button label',
            message: 'Access your prompt library',
        })}>
          <IconButton appearance="ghost" size="small" aria-label={t({
            message: 'Send',
            comment: 'Review chat - prompt library - aria label',
        })}>
            <Falcon icon="book-bookmark"/>
          </IconButton>
        </Tooltip>
      </TextAreaSpacer>
    </Container>);
}
// Exported entry for review chat
export function ReviewChat({ feedback }) {
    const userName = useUserName();
    const { messages, actions, chat } = useChatWithDocumentReview(feedback.feedback.id);
    const send = (userMessage) => chat(userMessage);
    return (<Chat send={send} messages={messages} actions={actions} userName={userName} feedback={feedback}/>);
}
